import { supabase } from "@/integrations/supabase/client";

// Mapping von Entity-Typen zu deutschen Kategorie-Namen
export const ENTITY_CATEGORY_MAP: Record<string, string> = {
  employees: "Stammdaten",
  salary_history: "Gehalt",
  bonus_history: "Bonus",
  benefits_history: "Benefits",
  evaluations: "Bewertung",
  criteria_ratings: "Bewertung", // Bewertungskriterien zur Bewertung gruppieren
  employee_clothing: "Arbeitskleidung",
  employee_tools: "Werkzeug",
  employee_perks: "Annehmlichkeiten",
  vehicle_costs: "KFZ-Kosten",
  employee_documents: "Dokumente",
  employee_time_tracking: "Stunden/Abwesenheit",
  employee_conversations: "Mitarbeitergespräch",
  employee_external_credentials: "Zugangsdaten",
  employee_mobile_phones: "Mobiltelefon",
  job_descriptions: "Stellenbeschreibung",
};

// Mapping von Aktionen zu deutschen Bezeichnungen
export const ACTION_LABEL_MAP: Record<string, string> = {
  INSERT: "hinzugefügt",
  UPDATE: "aktualisiert",
  DELETE: "entfernt",
  // New action values from triggers
  created: "hinzugefügt",
  updated: "aktualisiert",
  deleted: "entfernt",
  uploaded: "hochgeladen",
  downloaded: "heruntergeladen",
};

// Mapping von Feldnamen zu deutschen Bezeichnungen (aus AuditLogSettings.tsx übernommen)
export const FIELD_LABEL_MAP: Record<string, string> = {
  // Mitarbeiter-Stammdaten
  name: "Name",
  first_name: "Vorname",
  last_name: "Nachname",
  position: "Position",
  department: "Abteilung",
  employee_number: "Personalnummer",
  start_date: "Eintrittsdatum",
  end_date: "Austrittsdatum",
  birth_date: "Geburtsdatum",
  business_email: "Geschäfts-E-Mail",
  private_email: "Private E-Mail",
  phone: "Telefon",
  mobile: "Mobil",
  address_street: "Straße",
  address_zip: "PLZ",
  address_city: "Ort",
  address_country: "Land",
  nationality: "Nationalität",
  gender: "Geschlecht",
  marital_status: "Familienstand",
  children_count: "Anzahl Kinder",
  profile_image_url: "Profilbild",
  drivers_license: "Führerschein",
  drivers_license_classes: "Führerscheinklassen",
  tax_class: "Steuerklasse",
  tax_id: "Steuer-ID",
  social_security_number: "SV-Nummer",
  health_insurance_name: "Krankenkasse",
  health_insurance_number: "KK-Nummer",
  iban: "IBAN",
  bic: "BIC",
  bank_name: "Bank",
  emergency_contact_name: "Notfallkontakt",
  emergency_contact_phone: "Notfall-Telefon",
  notes: "Notizen",
  
  // Gehalt
  base_salary: "Grundgehalt",
  currency: "Währung",
  effective_date: "Gültig ab",
  reason: "Grund",
  salary_type: "Gehaltsart",
  hourly_rate: "Stundenlohn",
  weekly_hours: "Wochenstunden",
  
  // Bonus
  amount: "Betrag",
  bonus_type: "Bonusart",
  bonus_date: "Bonusdatum",
  description: "Beschreibung",
  period_start: "Zeitraum von",
  period_end: "Zeitraum bis",
  target_achieved: "Zielerreichung",
  approved_by: "Genehmigt von",
  approved_at: "Genehmigt am",
  paid_date: "Auszahlungsdatum",
  
  // Benefits
  benefit_type: "Benefit-Art",
  benefit_name: "Benefit-Name",
  monthly_value: "Monatswert",
  provider: "Anbieter",
  status: "Status",
  payment_frequency: "Zahlungsturnus",
  details: "Details",
  
  // Dokumente
  file_name: "Dateiname",
  category: "Kategorie",
  
  // Kleidung
  tshirt_size: "T-Shirt-Größe",
  pants_size: "Hosengröße",
  work_shoes_size: "Schuhgröße",
  winter_jacket_size: "Winterjacke",
  fleece_jacket_size: "Fleecejacke",
  sweatshirt_size: "Sweatshirt",
  helmet_size: "Helmgröße",
  gloves_size: "Handschuhe",
  safety_glasses: "Schutzbrille",
  ear_protection: "Gehörschutz",
  custom_items: "Sonderausstattung",
  
  // Werkzeuge
  serial_number: "Seriennummer",
  inventory_number: "Inventarnummer",
  assigned_date: "Ausgabedatum",
  is_assigned: "Zugewiesen",
  replacement_cost: "Wiederbeschaffungswert",
  
  // Zeiterfassung
  month: "Monat",
  monthly_hours: "Monatsstunden",
  vacation_days: "Urlaubstage",
  sick_days: "Krankheitstage",
  other_absence_days: "Sonstige Abwesenheit",
  
  // Annehmlichkeiten
  perk_type: "Art",
  perk_date: "Datum",
  
  // Bewertungen
  total_score: "Gesamtpunktzahl",
  max_score: "Maximalpunktzahl",
  performance_level: "Leistungsstufe",
  criteria: "Bewertungskriterien",
  date: "Datum",
  
  // Allgemein
  created_at: "Erstellt am",
  updated_at: "Geändert am",
  created_by: "Erstellt von",
};

export interface ChangeEntry {
  category: string;
  action: string;
  fields: string[];
  timestamp: string;
}

export interface GroupedChanges {
  [category: string]: {
    actions: string[];
    fields: string[];
  };
}

/**
 * Formatiert einen Feldnamen in eine lesbare deutsche Bezeichnung
 */
export const formatFieldName = (fieldName: string): string => {
  if (FIELD_LABEL_MAP[fieldName]) {
    return FIELD_LABEL_MAP[fieldName];
  }
  // Fallback: snake_case zu Title Case
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Holt die letzte Benachrichtigungszeit für einen Mitarbeiter
 */
export const getLastNotificationTime = async (employeeId: string): Promise<string | null> => {
  const { data, error } = await supabase
    .from('employee_notification_log')
    .select('last_notified_at')
    .eq('employee_id', employeeId)
    .order('last_notified_at', { ascending: false })
    .limit(1)
    .single();

  if (error || !data) {
    return null;
  }
  return data.last_notified_at;
};

/**
 * Speichert den Zeitpunkt einer Benachrichtigung
 */
export const saveNotificationLog = async (
  employeeId: string,
  sentBy: string,
  changesSummary: GroupedChanges
): Promise<void> => {
  await supabase.from('employee_notification_log').insert({
    employee_id: employeeId,
    sent_by: sentBy,
    changes_summary: changesSummary,
    last_notified_at: new Date().toISOString(),
  });
};

/**
 * Holt alle Änderungen seit der letzten Benachrichtigung
 */
export const fetchEmployeeChanges = async (
  employeeId: string
): Promise<{ changes: ChangeEntry[]; grouped: GroupedChanges; hasChanges: boolean }> => {
  // Letzte Benachrichtigungszeit holen
  const lastNotified = await getLastNotificationTime(employeeId);
  
  // Query für Audit-Log
  let query = supabase
    .from('entity_audit_log')
    .select('entity_type, action, changes, created_at')
    .eq('employee_id', employeeId)
    .neq('entity_type', 'criteria_ratings') // Einzelne Kriterien ausschließen - werden durch evaluations abgedeckt
    .order('created_at', { ascending: false });

  // Nur Änderungen seit der letzten Benachrichtigung
  if (lastNotified) {
    query = query.gt('created_at', lastNotified);
  }

  const { data, error } = await query;

  if (error || !data || data.length === 0) {
    return { changes: [], grouped: {}, hasChanges: false };
  }

  // Änderungen verarbeiten
  const changes: ChangeEntry[] = [];
  const grouped: GroupedChanges = {};

  for (const log of data) {
    const category = ENTITY_CATEGORY_MAP[log.entity_type] || log.entity_type;
    const action = ACTION_LABEL_MAP[log.action] || log.action;
    
    // Geänderte Felder extrahieren
    let fields: string[] = [];
    if (log.changes && typeof log.changes === 'object') {
      const changesObj = log.changes as { changed_fields?: string[] };
      if (changesObj.changed_fields && Array.isArray(changesObj.changed_fields)) {
        fields = changesObj.changed_fields.map(formatFieldName);
      }
    }

    changes.push({
      category,
      action,
      fields,
      timestamp: log.created_at,
    });

    // Gruppieren nach Kategorie
    if (!grouped[category]) {
      grouped[category] = { actions: [], fields: [] };
    }
    if (!grouped[category].actions.includes(action)) {
      grouped[category].actions.push(action);
    }
    fields.forEach(field => {
      if (!grouped[category].fields.includes(field)) {
        grouped[category].fields.push(field);
      }
    });
  }

  return { changes, grouped, hasChanges: changes.length > 0 };
};

/**
 * Erstellt eine formatierte Zusammenfassung der Änderungen für die E-Mail
 */
export const formatChangesForEmail = (grouped: GroupedChanges): string => {
  const lines: string[] = [];
  
  for (const [category, data] of Object.entries(grouped)) {
    const actionsText = data.actions.join(', ');
    const fieldsText = data.fields.length > 0 ? data.fields.join(', ') : '';
    
    if (fieldsText) {
      lines.push(`• ${category}: ${fieldsText} (${actionsText})`);
    } else {
      lines.push(`• ${category}: ${actionsText}`);
    }
  }
  
  return lines.join('\n');
};
